import settingsCheck from "@smartvatten/flowone-settings-check";
import axios from "axios";
import Cliser from "cliser";
import clone from "clone";
import Config from "config";
import Utils from "utils";
import Api from "utils/api";

const POINTER_PRECISIONS = [0.0001, 0.001, 0.01, 0.1];

const DIGIT_PRECISIONS = [
  1000000, 100000, 10000, 1000, 100, 10, 1, 0.1, 0.01, 0.001, 0.0001,
];

export const DEFAULT_SETTINGS = {
  alarms: {
    pointersFailRateLimit: 0,
  },
  camera: {
    backlightCompensation: "0",
    brightness: "62%",
    contrast: "27%",
    exposure: 200,
    fps: 15,
    frameCount: "1",
    gamma: "7",
    resolution: "640x480",
    saturation: "35%",
    sharpness: "50%",
    whiteBalanceTemperature: "0",
  },
  data: {
    sendData: 0,
    valueToUse: 0,
  },
  debug: {
    enabled: 0,
  },
  decimals: {
    enabled: 1,
    minimumCenterOffsetRatio: 1.5,
    minimumPotentiometerSize: 100,
    positions: [
      {
        coords: {
          x: 238,
          y: 407,
        },
        precision: 0.0001,
      },
      {
        coords: {
          x: 350,
          y: 459,
        },
        precision: 0.001,
      },
      {
        coords: {
          x: 441,
          y: 437,
        },
        precision: 0.01,
      },
      {
        coords: {
          x: 503,
          y: 365,
        },
        precision: 0.1,
      },
    ],
    redTargetPercentage: 2,
    redTreshold: 57,
    useCoords: 1,
  },
  digits: {
    enabled: 1,
    luminancethreshold: 20,
    localContrastW: 14,
    localContrastMargin: 0.11,
    thresholdForH: 4,
    thresholdForC: 4,
    redDigit: 0,
    noiseReduction: 1,
    cleaningFilter: 1,
    smoothing: 1,
    positions: [
      {
        coords: [243, 170, 264, 208],
        precision: 10000,
      },
      {
        coords: [287, 169, 308, 205],
        precision: 1000,
      },
      {
        coords: [333, 166, 354, 204],
        precision: 100,
      },
      {
        coords: [379, 166, 400, 203],
        precision: 10,
      },
      {
        coords: [423, 164, 445, 205],
        precision: 1,
      },
    ],
  },
  intervals: {
    dataStorageInterval: 10,
    dataUploadInterval: 1800,
    imageCaptureInterval: 30,
    sendPictureInterval: 0,
    sendStatisticsInterval: 86400,
  },
  orientation: 0,
  ts: 0,
};

export const DEFAULT_SETTINGS_CONST = clone(DEFAULT_SETTINGS);

function checkForNoChanges(key, values, originalValues, defaultValues) {
  if (
    (originalValues[key] === undefined || originalValues[key] === null) &&
    values[key] === defaultValues[key]
  ) {
    return true;
  }
  return false;
}

function patchSettings(settings, originalConfig) {
  if (!settings) return;
  if (settings.digits) {
    var digits = settings.digits;
    var originalDigits = {};
    if (
      originalConfig &&
      originalConfig.device &&
      originalConfig.device.settings &&
      originalConfig.device.settings.digits
    ) {
      originalDigits = originalConfig.device.settings.digits;
    }
    if (
      checkForNoChanges(
        "luminancethreshold",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.luminancethreshold;
    if (
      checkForNoChanges(
        "localContrastW",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.localContrastW;
    if (
      checkForNoChanges(
        "localContrastMargin",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.localContrastMargin;
    if (
      checkForNoChanges(
        "thresholdForH",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.thresholdForH;
    if (
      checkForNoChanges(
        "thresholdForC",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.thresholdForC;
    if (
      checkForNoChanges(
        "redDigit",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.redDigit;
    if (
      checkForNoChanges(
        "noiseReduction",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.noiseReduction;
    if (
      checkForNoChanges(
        "cleaningFilter",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.cleaningFilter;
    if (
      checkForNoChanges(
        "smoothing",
        digits,
        originalDigits,
        DEFAULT_SETTINGS.digits
      )
    )
      delete settings.digits.smoothing;
  }
  return settings;
}

function saveImageNeeded(settings, originalConfig) {
  const original =
    originalConfig && originalConfig.device && originalConfig.device.settings;
  if (original && settings) {
    const oDecimals = JSON.stringify(original.decimals);
    const oDigits = JSON.stringify(original.digits);
    const nDecimals = JSON.stringify(settings.decimals);
    const nDigits = JSON.stringify(settings.digits);
    return (
      oDecimals !== nDecimals ||
      oDigits !== nDigits ||
      original.orientation !== settings.orientation
    );
  }
  return false;
}

function distributeInvertedFlag(settings) {
  if (
    settings &&
    settings.digits &&
    settings.digits.positions &&
    settings.digits.inverted
  ) {
    settings.digits.positions.forEach((pos) => {
      if (!Number.isFinite(pos.inverted)) {
        pos.inverted = 1;
      }
    });
  }
  return settings;
}

function convertAutoConfigToPositions(result) {
  let positions = [];
  let prec = Math.pow(10, result.length - 1);
  for (let i = 0; i < result.length; i++) {
    let c = result[i].AABB.map((elem) => Math.floor(elem));
    let pos = {
      coords: c,
      precision: prec,
      inverted: 0,
    };
    positions.push(pos);
    prec = prec / 10;
  }
  return positions;
}

function scaleDigitPrecisions(positions, upOrDown) {
  if (positions) {
    // limit digit precision
    if (
      (upOrDown && positions[0].precision >= 100000) ||
      (!upOrDown && positions[positions.length - 1].precision <= 0.001)
    ) {
      return null;
    }
    let res = clone(positions);
    for (let i = 0; i < res.length; i++) {
      res[i].precision = upOrDown
        ? res[i].precision * 10
        : res[i].precision / 10;
    }
    return res;
  }
  return null;
}

function addDigitRect(positions, leftOrRight) {
  if (positions) {
    // cannot interpolate position
    if (positions.length < 2) {
      return null;
    }
    // limit digit precision
    if (
      (leftOrRight && positions[0].precision >= 100000) ||
      (!leftOrRight && positions[positions.length - 1].precision <= 0.001)
    ) {
      return null;
    }
    let res = clone(positions);
    let avgDist = 0;
    let avgWidth = 0;
    let avgHeight = 0;
    for (let i = 0; i < res.length; i++) {
      if (i < res.length - 1) {
        avgDist = avgDist + (res[i + 1].coords[0] - res[i].coords[0]);
      }
      avgWidth = avgWidth + (res[i].coords[2] - res[i].coords[0]);
      avgHeight = avgHeight + (res[i].coords[3] - res[i].coords[1]);
    }
    avgDist = Math.floor(avgDist / (res.length - 1));
    avgWidth = Math.floor(avgWidth / res.length);
    avgHeight = Math.floor(avgHeight / res.length);
    let newPos = {
      coords: [],
      precision: null,
      inverted: 0,
    };
    if (leftOrRight) {
      newPos.coords[0] = res[0].coords[0] - avgDist;
      newPos.coords[1] = res[0].coords[1];
      newPos.coords[2] = newPos.coords[0] + avgWidth;
      newPos.coords[3] = newPos.coords[1] + avgHeight;
      newPos.precision = res[0].precision * 10;
      res.unshift(newPos);
    } else {
      newPos.coords[0] = res[res.length - 1].coords[0] + avgDist;
      newPos.coords[1] = res[res.length - 1].coords[1];
      newPos.coords[2] = newPos.coords[0] + avgWidth;
      newPos.coords[3] = newPos.coords[1] + avgHeight;
      newPos.precision = res[res.length - 1].precision / 10;
      res.push(newPos);
    }
    return res;
  }
  return null;
}

export default class EditViewModel extends Cliser.Model {
  properties() {
    return {
      deviceId: "005074",
      date: new Date(),
      status: null,
      config: null,
      originalConfig: null,
      analyzedImage: null,
      deviceValues: {},
      deviceConnectionStatus: false,
      enabledPointers: this.enabledPointers.bind(this),
      enabledDigits: this.enabledDigits.bind(this),
      analyzeNeeded: false,
      error: null,
      deviceVersion: null,
      lifecycleState: null,
      premise: null,
      latestImageLink: null,
      multipleClients: null,
      openServiceRequests: [],
      showUnreliableMeterDialog: false,
      unreliableMeterInfo: null,
      deviceHw: null,
    };
  }

  init() {
    this.session = Cliser.open().listen("DeviceModel", (state) => {
      this.deviceValues = state.values;
      this.deviceVersion = state.version;
      this.deviceConnectionStatus = state.connected;
      this.multipleClients = state.multipleClients;
      this.deviceHw = state.deviceHw;
      if (this.config && state.values.sendData !== undefined) {
        this.setSetting("data.sendData", state.values.sendData);
      }
      this.emit();
    });
  }

  uninit() {
    this.session.close();
  }

  /*
   * Load device
   */
  static loadDevice(id) {
    this.deviceId = id || this.deviceId;
    document.title = this.deviceId + " FlowOne Setup";

    // Reset state
    this.status = "connectingDevice";
    this.config = null;
    this.deviceVersion = null;
    this.originalConfig = null;
    this.analyzedImage = null;
    this.image = null;
    this.error = null;
    this.lifecycleState = null;
    this.premise = null;
    this.latestImageLink = null;
    this.openServiceRequests = [];

    return Promise.all([
      Cliser.ConfigModel.loadConfigForDevice(this.deviceId, this.date),
    ])
      .then(([config]) => {
        Cliser.ConfigModel.lifecycleState(this.deviceId).then((res) => {
          this.lifecycleState = res.events[0].state;
        });
        Cliser.ConfigModel.premise(this.deviceId).then((res) => {
          this.premise = res;
        });
        Cliser.DeviceModel.connectDevice(this.deviceId, config.device.server);
        Cliser.ConfigModel.latestImageLink(this.deviceId).then((res) => {
          this.latestImageLink = res;
        });
        Cliser.ConfigModel.openServiceRequests(this.deviceId).then((res) => {
          //console.log("Open service requests:", res);
          this.openServiceRequests = res;
        });
        this.originalConfig = clone(config);
        //console.log("orig config", this.originalConfig);
        // Use default settings if proper settings are not set to meta.
        let settings = Object.assign({}, DEFAULT_SETTINGS);
        if (config && config.device && config.device.settings) {
          Object.assign(settings, config.device.settings);
        }
        settings = distributeInvertedFlag(settings);
        config.device.settings = settings;
        config.device.settings = patchSettings(settings, this.originalConfig);

        this.config = config;
        this.ensurePointerCoordinates();
      })
      .then(() => this.setImageSource("stream"))
      .catch((err) => {
        this.errorHandler(err);
      });
  }

  /*
   * Analyze images
   */
  static analyzeImage() {
    this.status = "analyzing";
    this.error = null;
    this.analyzedImage = null;
    this.emit();

    return Cliser.ImageModel.analyze(this.config.device.settings)
      .then((results) => {
        console.log("results", results);
        this.analyzeNeeded = false;
        //check config for possible errors
        try {
          results.digitsConfigWarning = this.checkDigitsConfig(
            this.config.device.settings.digits.positions
          );
          results.pointersConfigWarning = this.checkPointerConfig(
            this.config.device.settings.decimals.positions
          );
        } catch (err) {
          console.warn("Failed to validate digits or pointer config.", err);
        }
        this.analyzedImage = results;
        // temporary copy pointers object to main level
        if (results.pointers) {
          for (var key in results.pointers) {
            this.analyzedImage[key] = results.pointers[key];
          }
        }
        this.status = null;
        this.emit();
      })
      .catch(this.errorHandler.bind(this));
  }

  static generateAutoConfig() {
    this.status = "Generating autoconfig...";
    this.error = null;
    return Cliser.ImageModel.autoConfig()
      .then((result) => {
        let res = convertAutoConfigToPositions(result);
        console.log("converted digits result", res);
        this.config.device.settings.digits.positions = res;
        this.status = null;
      })
      .then(() => this.analyzeImage())
      .catch(this.errorHandler.bind(this));
  }

  static scaleDigitPrecisionsUp() {
    let res = scaleDigitPrecisions(
      this.config.device.settings.digits.positions,
      true
    );
    if (res) {
      this.config.device.settings.digits.positions = res;
    }
  }

  static scaleDigitPrecisionsDown() {
    let res = scaleDigitPrecisions(
      this.config.device.settings.digits.positions,
      false
    );
    if (res) {
      this.config.device.settings.digits.positions = res;
    }
  }

  static addDigitLeft() {
    let res = addDigitRect(this.config.device.settings.digits.positions, true);
    if (res) {
      this.config.device.settings.digits.positions = res;
    }
  }

  static addDigitRight() {
    let res = addDigitRect(this.config.device.settings.digits.positions, false);
    if (res) {
      this.config.device.settings.digits.positions = res;
    }
  }

  static reloadImage() {
    this.status = "loadingImage";
    this.error = null;
    this.analyzedImage = null;
    return Cliser.ImageModel.refresh()
      .then(() => this.analyzeImage())
      .catch(this.errorHandler.bind(this));
  }

  static setImageSource(source) {
    this.status = "loadingImage";
    this.analyzedImage = null;
    return Cliser.ImageModel.setSource(source)
      .then(() => this.analyzeImage())
      .catch(this.errorHandler.bind(this));
  }

  static setSettings(settings) {
    var sett = Object.assign(
      {},
      DEFAULT_SETTINGS,
      Utils.convertBoolsToInts(settings)
    );
    //sett = patchSettings(settings, this.originalConfig);
    Utils.setSafely(this.config, "device.settings", sett);
  }

  static deleteSetting(setting) {
    Utils.deleteSafely(this.config, "device.settings", setting);
  }

  static createServiceRequest(serviceRequestClass, comment) {
    let request = {
      premiseId: this.premise._id,
      deviceId: this.deviceId,
      premiseName: this.premise.name,
      created: {
        comment,
        serviceRequestClass
      }
    }
    return Cliser.ConfigModel.createServiceRequest(request).then(() => {
      return Cliser.ConfigModel.openServiceRequests(this.deviceId).then((res) => {
        //console.log("Open service requests:", res);
        this.openServiceRequests = res;
        this.emit();
      });
    })
  }

  static saveSettingsToDevice(lifecycleState, configMeta) {
    this.config.device.settings = patchSettings(
      this.config.device.settings,
      this.originalConfig
    );
    this.status = "saving";
    this.config.device.settings.ts = Date.now();
    //always save image to have correct camera settings for auto movement fixer.
    const doSave = true; //saveImageNeeded(this.config.device.settings, this.originalConfig);
    try {
      settingsCheck(this.config.device.settings);
      return Cliser.ConfigModel.saveSettingsToDevice(
        this.config.device.id,
        this.config.device.settings,
        lifecycleState,
        doSave,
        configMeta
      )
        .then(() => {
          this.resetError();
          this.status = null;
          this.lifecycleState = null;
          Cliser.ConfigModel.lifecycleState(this.deviceId).then((res) => {
            this.lifecycleState = res.events[0].state;
            this.emit();
          });
          this.originalConfig = clone(this.config);
        })
        .catch(this.errorHandler.bind(this));
    } catch (error) {
      this.errorHandler(error);
    }
  }

  static savePublicNoteToMetaData(meta) {
    try {
      return Cliser.ConfigModel.savePublicNoteToMeta(
        meta,
        this.config.device.id
      );
    } catch (err) {
      console.log("Error saving public config note:", err);
    }
  }

  static updateFlowoneDigits(deviceId, intergers, decimals) {
    console.log(
      "updateFlowoneDigits for deviceId:",
      deviceId,
      "i:",
      intergers,
      "d:",
      decimals
    );
    console.log("value:", parseInt(intergers) + parseFloat("0." + decimals));
    return Api.request({
      url: Config.servers.flowOneApi + "/lifecycle/data/flowonedigits",
      method: "POST",
      body: {
        deviceId,
        digitsValue: intergers,
        decimals,
        source:"setup-assistant"
      },
      apitoken: Config.apiKeys.flowOneApiKey,
    });
  }

  static reportUnreliableMeter(params) {
    this.status = "saving";
    try {
      return Cliser.ConfigModel.unreliableMeter(params)
        .then(() => {
          this.resetError();
          this.status = null;
          return Cliser.ConfigModel.premise(this.deviceId);
        })
        .then((res) => {
          this.premise = res;
        })
        .catch(this.errorHandler.bind(this));
    } catch (error) {
      this.errorHandler(error);
    }
  }

  /*
   * Set new digits & decimal coordinates
   */
  static setNewPositions(newPositions) {
    if (newPositions.decimals) {
      this.editArraySetting("decimals.positions", (position, index) => {
        position.coords = newPositions.decimals[index] || null;
        return position;
      });
    }
    if (newPositions.digits) {
      this.editArraySetting("digits.positions", (position, index) => {
        position.coords = newPositions.digits[index] || null;
        return position;
      });
    }
  }

  /**
   * Reset decimal coordinates
   */

  static resetDecimalPositions() {
    this.editArraySetting("decimals.positions", (position) => {
      delete position.coords;
      return position;
    });
    this.ensurePointerCoordinates();
  }

  /**
   * Set setting value
   */
  static setSetting(path, value) {
    Utils.setSafely(
      this.config,
      "device.settings." + path,
      Utils.convertBoolsToInts(value)
    );
    if (path === "camera.resolution") {
      this.error = "Warning: Camera resolution changed, please reload image.";
      this.emit();
    }
  }
  /**
   *
   * Delete settings
   */
  static deleteSetting(path) {
    Utils.deleteSafely(this.config, "device.settings." + path);
  }

  static setDataSend(enabled) {
    Cliser.DeviceModel.setDataSend(enabled).then(() =>
      this.saveSettingsToDevice()
    );
  }

  static setTerminalState(enabled) {
    this.setSetting("device.terminalOn",enabled)
    this.saveSettingsToDevice()
  }


  static setPrecisionEnabled(precision, enabled, entity) {
    let params =
      entity >= "digits"
        ? {
          digits: true,
          path: "digits.positions",
          precisions: DIGIT_PRECISIONS,
        }
        : {
          decimals: true,
          path: "decimals.positions",
          precisions: POINTER_PRECISIONS,
        };

    let positions = this.getSetting(params.path) || [];

    let newPositions = params.precisions
      .map((np) => {
        let pos = positions.find((op) => op.precision === np);
        if (np === precision) {
          if (enabled) {
            if (!pos) {
              pos = { precision: np };
            }
          } else {
            pos = null;
          }
        }
        return pos;
      })
      .filter((p) => p);

    this.setSetting(params.path, newPositions);
    if (params.decimals) {
      this.setSetting("decimals.useCoords", false);
    }
    this.ensurePointerCoordinates();
    this.analyzeNeeded = true;
  }
  static enableItronMode(precision, enabled) {
    console.log("enableItronMode:", precision, enabled);
    let positions = this.getSetting("digits.positions");
    let newPositions = DIGIT_PRECISIONS.map((np) => {
      let pos = positions.find((op) => op.precision === np);
      if (np === precision) {
        if (pos) {
          pos.itronMode = enabled;
        }
        return pos;
      }
    }).filter((p) => p);
  }

  static setPrecisionInverted(precision, inverted) {
    let params = {
      digits: true,
      path: "digits.positions",
      precisions: DIGIT_PRECISIONS,
    };

    let positions = this.getSetting(params.path) || [];

    let newPositions = params.precisions
      .map((np) => {
        let pos = positions.find((op) => op.precision === np);
        if (np === precision) {
          if (pos) {
            if (inverted || Number.isFinite(pos.inverted)) {
              pos.inverted = inverted;
            }
          }
        }
        return pos;
      })
      .filter((p) => p);

    this.setSetting(params.path, newPositions);
    this.analyzeNeeded = true;
  }

  /**
   * Set device value
   */
  static setMeterValue(integers, decimals, deviceId) {
    console.log("setMeterValue");
    const data = {};
    if (integers && integers.length > 0) {
      data.integers = integers;
    }
    if (decimals && decimals.length > 0) {
      data.decimals = decimals;
    }
    const promises = [];
    promises.push(
      Cliser.ImageModel.getImageData().then((file) => {
        data.file = file;
        return Cliser.ConfigModel.saveImage(data, deviceId);
      })
    );
    promises.push(Cliser.DeviceModel.setMeterValue(integers, decimals));
    promises.push(this.updateFlowoneDigits(deviceId, integers, decimals));
    return Promise.all(promises).catch(this.errorHandler.bind(this));
  }

  static resetError() {
    this.error = null;
  }

  static rebootDevice() {
    this.status = "rebooting";
    Cliser.DeviceModel.rebootDevice()
      .then(() => {
        this.status = "waiting";
        this.emit();
        setTimeout(() => {
          this.status = null;
          this.reloadImage();
        }, 90000);
      })
      .catch(this.errorHandler.bind(this));
  }

  static restartDeviceSw() {
    Cliser.DeviceModel.restartDeviceSw();
  }

  static sendCustomCmd(command, params) {
    return Cliser.DeviceModel.sendCustomCmd(command, params);
  }

  // Utils methods for easier settings manipulation

  getSetting(path) {
    return Utils.getSafely(this, "config.device.settings." + path);
  }

  editArraySetting(path, iterator) {
    this.setSetting(path, (this.getSetting(path) || []).map(iterator));
  }

  enabledPointers() {
    return this.getPrecisionList(POINTER_PRECISIONS, "decimals.positions");
  }

  enabledDigits() {
    let digits = this.getPrecisionList(DIGIT_PRECISIONS, "digits.positions");
    return digits;
  }

  getPrecisionList(precisions, path) {
    let ptrs = precisions.map((p) => ({ precision: p, enabled: false }));
    let positions = this.getSetting(path);

    // if (positions) {
    //   let firstIdx, lastIdx, allTogglable;
    //   if (positions.length === 0) {
    //     allTogglable = true;
    //   } else {
    //     let first = positions[0];
    //     let last = positions[positions.length - 1];
    //     firstIdx = ptrs.findIndex((p) => p.precision === first.precision);
    //     lastIdx = ptrs.findIndex((p) => p.precision === last.precision);
    //   }
    //   ptrs.forEach((ptr, idx) => {
    //     let pos = positions.find((p) => p.precision === ptr.precision);
    //     ptr.enabled = !!pos;
    //     ptr.togglable = allTogglable || idx === firstIdx || idx === firstIdx - 1 || idx === lastIdx || idx === lastIdx + 1;
    //   });
    // } else {
    //   ptrs.forEach((ptr) => ptr.togglable = true);
    // }
    if (positions) {
      ptrs.forEach((ptr, idx) => {
        let pos = positions.find((p) => p.precision === ptr.precision);
        ptr.enabled = !!pos;
        ptr.togglable = true;
        if (ptr.enabled) {
          ptr.inverted = pos.inverted;
        }
        if (pos && pos.itronMode) {
          ptr.itronMode = pos.itronMode;
        }
      });
    }
    return ptrs;
  }

  ensurePointerCoordinates() {
    let positions = this.getSetting("decimals.positions");
    let analyzed = this.analyzedImage;

    if (positions) {
      positions = positions.map((p, idx) => {
        if (!p.coords) {
          if (analyzed && analyzed.meters[idx]) {
            let center = analyzed.meters[idx].center;
            p.coords = { x: parseInt(center.x), y: parseInt(center.y) };
          }
        }
        return p;
      });
    }

    this.setSetting("decimals.positions", positions);
  }

  checkDigitsConfig(positions) {
    let copypositions = JSON.parse(JSON.stringify(positions));
    let ordered = copypositions.sort((a, b) => a.precision < b.precision);
    let prev = null;
    let orderOk = true;
    for (let item of ordered) {
      if (
        prev &&
        prev.coords[0] > item.coords[0] &&
        !item.itronMode &&
        !prev.itronMode
      ) {
        orderOk = false;
        break;
      }
      prev = item;
    }

    if (!orderOk) {
      return "Warning: Check order of digits!";
    } else {
      return null;
    }
  }

  checkPointerConfig(positions) {
    let copypositions = JSON.parse(JSON.stringify(positions));
    let ordered = copypositions.sort((a, b) => a.precision > b.precision);
    let prev = null;
    let orderok = true;
    for (let item of ordered) {
      if (prev && prev.coords.x > item.coords.x) {
        orderok = false;
        break;
      }
      prev = item;
    }

    if (!orderok) {
      return "Warning: Check order of pointers!";
    } else {
      return null;
    }
  }

  errorHandler(err) {
    // TODO debugging becomes a pain in the ass, if err is not thrown
    // throw err
    console.error(err);

    this.status = null;
    let msg = "";
    if (err.message) {
      msg = err.message;
    } else if (err.error) {
      msg = err.error;
    } else {
      msg = "unknown error";
    }
    this.error = "Error: " + msg;
    this.emit();
  }

  static autoConfig() {
    console.log("Auto config running...");
    this.status = "autoconfig";
    this.emit();
    return Cliser.ImageModel.getImageData().then((file) => {
      //console.log("blob?:", file);
      //console.log("blob?:", typeof (file));
      return getBase64(file).then((b64) => {
        return Api.request({
          url: Config.servers.flowOneApi + "/lifecycle/gtoken",
          method: "GET",
          apitoken: Config.apiKeys.flowOneApiKey,
        })
          .then((gtoken) => {
            //console.log("token", gtoken);
            let datas = b64.split("data:application/octet-stream;base64,")[1];
            //console.log("b64:", datas);
            let data = {
              instances: [
                {
                  b64: datas,
                },
              ],
            };
            let options = {
              method: "POST",
              headers: {
                "content-type": "application/json",
                Authorization: "Bearer " + gtoken,
              },
              data,
              url: `https://europe-west1-aiplatform.googleapis.com/v1/projects/${Config.ml.project}/locations/europe-west1/endpoints/${Config.ml.endpointId}:predict`,
            };
            //console.log(data)
            return axios(options).then((settings) => {
              console.log("Autoconfig settings:", settings.data.predictions);
              //console.log(JSON.stringify(settings.data.predictions));
              if (!settings.data.predictions) {
                console.warn("Auto config failed: empty response");
                this.status = null;
                this.error = "Auto config failed";
                this.emit();
                return;
              }
              if (settings && settings.data && settings.data.predictions) {
                let predictions = settings.data.predictions[0];
                let boxes = predictions.boxes;
                let angle = predictions.angle;
                let digitPositions =
                  this.config.device.settings.digits.positions;
                let decimalPositions =
                  this.config.device.settings.decimals.positions;
                let oldDecimalPositions = JSON.parse(JSON.stringify(this.config.device.settings.decimals.positions))
                let oldDigitPositions = JSON.parse(JSON.stringify(this.config.device.settings.digits.positions))
                let addingDigits = false;
                let addingDecimals = false;
                for (let i = 0; i < boxes.length; i++) {
                  let digitPos = digitPositions.find((x) => {
                    return (
                      x.precision === getDigitPrecision(predictions.classes[i])
                    );
                  });
                  if (digitPos) {
                    console.log("Modifying current digit coords");
                    digitPos.coords = roundToIntegers(boxes[i]);
                    digitPos.modified = true;
                  } else if (getDigitPrecision(predictions.classes[i])) {
                    console.log(
                      "Adding new digit:",
                      getDigitPrecision(predictions.classes[i])
                    );
                    addingDigits = true;
                    digitPositions.push({
                      coords: roundToIntegers(boxes[i]),
                      precision: getDigitPrecision(predictions.classes[i]),
                      added: true,
                    });
                  }

                  let decimalPos = decimalPositions.find((x) => {
                    return (
                      x.precision ===
                      getDecimalsPrecision(predictions.classes[i])
                    );
                  });
                  if (decimalPos) {
                    console.log("Modifying current pointer coords");
                    decimalPos.coords = getCenterPoint(boxes[i]);
                    decimalPos.modified = true;
                  } else if (getDecimalsPrecision(predictions.classes[i])) {
                    console.log(
                      "Adding new decimal:",
                      getDecimalsPrecision(predictions.classes[i])
                    );
                    addingDecimals = true;
                    decimalPositions.push({
                      coords: getCenterPoint(boxes[i]),
                      precision: getDecimalsPrecision(predictions.classes[i]),
                      added: true
                    });
                  }
                }
                checkAndMoveUndetectedPointers(oldDecimalPositions, decimalPositions);
                checkAndMoveUndetectedDigits(oldDigitPositions, digitPositions);
                //delete added auto config data
                for (let pos of digitPositions) {
                  delete pos.modified;
                  delete pos.added;
                }
                for (let pos of decimalPositions) {
                  delete pos.modified;
                  delete pos.added;
                }
                if (addingDigits) {
                  //set digits in correct order
                  console.log("sorting digits...");
                  digitPositions.sort((a, b) => b.precision - a.precision);
                }
                if (addingDecimals) {
                  console.log("sorting decimals...");
                  decimalPositions.sort((a, b) => a.precision - b.precision);
                }
                //}
                console.log("Digits:", digitPositions);
                //let newsettings=this.setNewPositions(digits);
                //this.setSetting('digits.positions', digits.positions);

                if (angle != 0) {
                  console.log(
                    "Old angle:",
                    this.config.device.settings.orientation
                  );
                  console.log("ML Angle", angle);
                  let newAngle = getRotation(
                    this.config.device.settings.orientation,
                    angle
                  );
                  //let newAngle = angle;
                  console.log("New Angle:", newAngle);
                  this.setSetting("orientation", newAngle);
                  this.reloadImage();
                } else {
                  this.analyzeImage();
                }
                console.log("auto config done!");
              }
            });
          })
          .catch((error) => {
            console.warn("Auto config failed:", error);
            this.status = null;
            this.error = "Auto config failed";
            this.emit();
          });
      });
    });
  }
}

const roundToIntegers = (arr) => {
  const res = [];
  arr.forEach((el, ind) => {
    res[ind] = Math.round(el);
  });
  return res;
};

const checkAndMoveUndetectedDigits = (oldDigitPositions, digitPositions) => {
  let dX = 0;
  let dY = 0;
  let count = 0;
  const unmodifiedPositions = [];
  for (const pos of digitPositions) {
    if (pos.modified) {
      let oldPos = oldDigitPositions.find((i) => i.precision == pos.precision);
      if (oldPos) {
        //we are only using one coordinate for calculations. Should we use both?
        dX += pos.coords[0] - oldPos.coords[0];
        dY += pos.coords[1] - oldPos.coords[1];
        count++;
      }
    }
    else if (!pos.added) {
      console.log("unmodified pointer" + pos.precision)
      unmodifiedPositions.push(pos)
    }
  }
  if (count > 0) {
    dX = dX / count;
    dY = dY / count;
    console.log(`digit deltas x:${dX}, y:${dY} count:${count}`)
  }
  if (unmodifiedPositions.length && count) {
    for (const pos of unmodifiedPositions) {
      pos.coords[0] = Math.round(pos.coords[0] + dX);
      pos.coords[1] = Math.round(pos.coords[1] + dY);
      pos.coords[2] = Math.round(pos.coords[2] + dX);
      pos.coords[3] = Math.round(pos.coords[3] + dY);
      console.log(`DEBUG: modifing undetected digit ${pos.precision} dx:${dX} dy:${dY}`);
    }
  }

}

const checkAndMoveUndetectedPointers = (oldDecimalPositions, decimalPositions) => {
  let dX = 0;
  let dY = 0;
  let count = 0;

  const unmodifiedPositions = [];
  //check if we have non modified positions
  for (const pos of decimalPositions) {
    if (pos.modified) {
      let oPos = oldDecimalPositions.find((i) => i.precision == pos.precision);
      if (oPos) {
        dX += pos.coords.x - oPos.coords.x;
        dY += pos.coords.y - oPos.coords.y;
        count++;
      }

    } else if (!pos.added) {
      unmodifiedPositions.push(pos)
    }
  }

  if (count > 0) {
    dX = dX / count;
    dY = dY / count;
    console.log(`deltas x:${dX}, y:${dY} count:${count}`)
  }
  if (unmodifiedPositions.length && count) {
    for (const pos of unmodifiedPositions) {
      pos.coords.x = Math.round(pos.coords.x + dX);
      pos.coords.y = Math.round(pos.coords.y + dY);
      console.log(`DEBUG: modifing undetected pointer ${pos.precision} x:${pos.coords.x} y:${pos.coords.y}`)
      let oPos = oldDecimalPositions.find((i) => i.precision == pos.precision);
      if (oPos) {
        console.log(`DEBUG: old pointer ${oPos.precision} x:${oPos.coords.x} y:${oPos.coords.y}`)
      }
    }
  }
}

const getDigitPrecision = (ind) => {
  switch (ind) {
    case 0:
      return 100000;
    case 1:
      return 10000;
    case 2:
      return 1000;
    case 3:
      return 100;
    case 4:
      return 10;
    case 5:
      return 1;
    case 6:
      return 0.1;
    case 7:
      return 0.01;
    case 8:
      return 0.001;
    case 9:
      return 0.0001;
    default:
      return null;
  }
};

const getDecimalsPrecision = (ind) => {
  switch (ind) {
    case 10:
      return 0.1;
    case 11:
      return 0.01;
    case 12:
      return 0.001;
    case 13:
      return 0.0001;
    default:
      return null;
  }
};

const getCenterPoint = (rect) => {
  return {
    x: Math.round((rect[2] + rect[0]) / 2),
    y: Math.round((rect[3] + rect[1]) / 2),
  };
};

const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const getRotation = (oR, nR) => {
  //let adjustedNewRotation = 360 - nR;
  console.log("New angle debug nR, oR", nR, oR);
  let newAngle = parseFloat(parseFloat(oR) + nR).toFixed(1);
  newAngle = newAngle % 360
  if (newAngle > 180) {
    newAngle = newAngle - 360;
  }
  //let newAngle = nR;
  //newAngle = parseFloat(parseFloat(newAngle) - 180).toFixed(1);

  /*if (newAngle > 360) {
    newAngle = parseFloat(parseFloat(newAngle) - 360).toFixed(1);
  } else if(newAngle>180) {
    newAngle = parseFloat(parseFloat(newAngle) - 180).toFixed(1);
  }*/
  return newAngle;
};



// WEBPACK FOOTER //
// ./client/models/EditViewModel.js